/**
 * Comment les heures d'un jour se répartissent entre le matin et l'après-midi.
 *
 * *Demande de Chris, 04/09/2026 : « dans le tableau de bord des affectations on voit bien 4 h
 * mais on n'a pas l'info que c'est 2 h matin et 2 h après-midi ; il faudrait mentionner cela
 * dans le tableau de bord plan de charge, dans la colonne heure peut-être séparer matin et
 * après-midi en gardant aussi l'info du total. »*
 *
 * ## Une seule règle, deux écrans
 *
 * Le planning des ressources répartissait déjà la charge entre les moments occupés, et le plan
 * de charge n'en montrait que le total : la même question posée à deux endroits, avec une seule
 * des deux réponses écrite. La division est donc extraite ici, et les deux la lisent.
 *
 * L'écrire deux fois aurait fini par répartir autrement d'un écran à l'autre — c'est exactement
 * l'écart que Chris est venu signaler, et la raison pour laquelle le produit tient partout la
 * règle « un seul calcul, plusieurs lectures ».
 *
 * ## La répartition est ÉGALE entre les moments occupés
 *
 * Elle n'est pas une supposition : une demi-journée pèse quatre heures, toujours (`base 4 h par
 * ½ journée`, PRD §4.7). Deux moments occupés portent donc deux demi-journées, et se partagent
 * les heures du jour à parts égales.
 *
 * Les moments **non occupés n'apparaissent pas** : un « 0 h matin » se lirait comme une charge
 * mesurée à zéro, alors que rien n'est posé ce matin-là.
 */
import { dec, formaterNombre } from "@planif/moteur";
import type { Position } from "@planif/moteur";

/**
 * Une décimale, comme les cases du planning des ressources.
 *
 * Et non les quatre de `formaterHeure`, qui sont la précision d'un chiffrage : c'est ici une
 * note de lecture, sous un total qui porte déjà la précision. « 4,0000 h matin » se lit comme
 * un calcul, alors que la seule question posée est de savoir laquelle des deux moitiés est
 * prise. La grandeur est de toute façon un multiple de quatre heures — une demi-journée pèse
 * 4 h — donc aucune décimale n'est perdue.
 */
const DECIMALES_DE_LA_NOTE = 1;

/** Ce qu'un moment de la journée porte. */
export type PartDuMoment = {
  readonly position: Position;
  readonly heures: string;
};

/** Comment un moment se nomme, dans une phrase. */
export function nommerLeMoment(position: Position): string {
  return position === "matin" ? "matin" : "après-midi";
}

/**
 * Le moment qu'une HEURE LIMITE désigne.
 *
 * *Demande de Chris, 04/09/2026 : « présente comme dans l'exploitation la colonne des heures en
 * spécifiant matin et après-midi […] et fais pareil pour transport acheminement articles ».*
 *
 * ## Pourquoi il fallait une seconde règle, et pas la première
 *
 * Une rotation et une livraison ne portent **aucune demi-journée** : elles ont un jour et une
 * heure limite, jamais un matin — c'est la règle du 03/09, et elle ne bouge pas. La coupe de
 * l'exploitation, qui répartit des heures sur les moments occupés, ne pouvait donc rien dire
 * ici : le tableau du transport restait muet là où celui de l'exploitation parlait.
 *
 * **Mais l'heure limite dit bien quelque chose** : un camion attendu avant midi vient le matin,
 * un chargement à 15 h vient l'après-midi. Ce n'est pas la même affirmation — on ne prétend pas
 * que la rotation *occupe* la demi-journée, on dit **quand elle est attendue** —, et c'est
 * exactement ce que le responsable transport organise.
 *
 * Midi appartient au MATIN : « avant 12:00 » est une échéance de matinée, et la ranger
 * l'après-midi ferait manquer le seul créneau où le camion doit être passé.
 *
 * `null` quand aucune heure n'est saisie : l'heure est alors indifférente, et inventer un moment
 * ferait organiser une tournée autour d'une contrainte qui n'existe pas.
 */
export function momentDeLHeureLimite(heureLimite: string | null): Position | null {
  if (heureLimite === null || heureLimite.trim() === "") return null;
  const heures = Number(heureLimite.slice(0, 2));
  const minutes = Number(heureLimite.slice(3, 5));
  if (!Number.isFinite(heures) || !Number.isFinite(minutes)) return null;
  return heures * 60 + minutes <= 12 * 60 ? "matin" : "apres_midi";
}

/**
 * Ce qu'une échéance dit du moment, en toutes lettres — ou `null` s'il n'y a rien à en dire.
 *
 * **« attendu le matin », et non « matin » tout court.** Sur une ligne d'exploitation, le mot
 * seul veut dire que la ressource occupe la demi-journée ; ici il voudrait dire que le camion y
 * passe la matinée, ce qui est faux — il passe à une heure. Le verbe fait toute la différence, et
 * les deux phrases se lisent à trois centimètres l'une de l'autre dans la même colonne.
 *
 * L'heure elle-même n'est pas répétée : elle est déjà affichée à côté du total.
 */
export function direLeMomentDeLEcheance(heureLimite: string | null): string | null {
  const moment = momentDeLHeureLimite(heureLimite);
  if (moment === null) return null;
  return `attendu ${moment === "matin" ? "le matin" : "l'après-midi"}`;
}

/**
 * Les heures d'un jour, telles que le serveur les a réparties — divisées par l'effectif.
 *
 * ## La division par le nombre de moments a disparu, et c'était une faute en attente
 *
 * L'écran divisait le total du jour par le nombre de demi-journées occupées. C'était exact
 * tant que le plan de charge comptait `demi-journées × 4 h` : toutes les moitiés pesaient
 * alors le même poids, par construction. **Depuis que les heures chiffrées se répartissent en
 * remplissant** (décision de Chris du 04/09/2026), une matinée peut porter 4 h quand
 * l'après-midi n'en porte que 2 — et la division aurait annoncé 3 h et 3 h, un total juste
 * fait de deux nombres faux.
 *
 * Le détail vient donc du même calcul que le total (`moments`), et non d'une seconde
 * arithmétique côté écran. C'est la règle que ce produit tient partout : *un seul calcul,
 * plusieurs lectures*.
 *
 * `parExemplaire` divise la part de la journée par l'effectif déclaré — c'est ce que le
 * planning des ressources veut savoir (*ce que CETTE personne fait*), là où le plan de charge
 * affiche le total de tous réunis. Un pour ne rien diviser.
 *
 * Vide quand le besoin ne porte aucun moment — une rotation ou une livraison a une heure
 * limite, pas un matin. En inventer un ferait lire « pris le matin » là où un camion passe
 * à 15 h.
 */
export function repartirSurLesMoments(
  moments: readonly PartDuMoment[],
  parExemplaire = 1,
): readonly PartDuMoment[] {
  const compte = Number.isInteger(parExemplaire) && parExemplaire > 0 ? parExemplaire : 1;
  if (compte === 1) return moments;
  return moments.map((moment) => ({
    position: moment.position,
    heures: dec(moment.heures).div(compte).toFixed(),
  }));
}

/**
 * La répartition en toutes lettres, ou `null` s'il n'y a rien à ajouter au total.
 *
 * **Un seul moment occupé n'affiche pas ses heures, seulement son nom** : elles sont déjà le
 * total affiché à côté, et les répéter ferait chercher ce qui distingue les deux nombres. Ce
 * qui manquait dans ce cas-là n'était pas une quantité, c'était de savoir *quand* — une
 * demi-journée du matin et une de l'après-midi pèsent pareil et ne s'affectent pas pareil.
 */
export function direLaRepartition(moments: readonly PartDuMoment[]): string | null {
  const parts = moments;
  if (parts.length === 0) return null;
  if (parts.length === 1) return nommerLeMoment(parts[0]?.position ?? "matin");
  return parts
    .map(
      (part) =>
        `${formaterNombre(part.heures, DECIMALES_DE_LA_NOTE)} h ${nommerLeMoment(part.position)}`,
    )
    .join(" · ");
}
